import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, X, ArrowRight, ChevronRight } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { Container } from '@/components/ui/Container';
import { SectionHeading } from '@/components/ui/SectionHeading';
import { useTreatments } from '@/lib/useTreatments';
import { formatPula } from '@/lib/money';
import { useReveal } from '@/lib/useReveal';
import { PageHeader } from '@/components/ui/PageHeader';
import type { Treatment } from '@/data/treatments';

const FALLBACK = 'A tailored, unhurried treatment by our skilled therapists. Ask us anything when you book.';

export function Services() {
  useReveal();
  const { categories, winterSpecials } = useTreatments();
  const [active, setActive] = useState<Treatment | null>(null);

  const Row = ({ t }: { t: Treatment }) => (
    <button onClick={() => setActive(t)} className="group flex w-full items-baseline justify-between gap-6 py-5 text-left transition hover:bg-paper">
      <div className="flex items-baseline gap-3">
        <h3 className="font-display text-xl text-espresso transition group-hover:text-gold-deep sm:text-2xl">{t.name}</h3>
        {t.durationMin && (
          <span className="hidden items-center gap-1 font-body text-xs uppercase tracking-wide text-muted sm:inline-flex">
            <Clock size={13} /> {t.durationMin} min
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2 whitespace-nowrap">
        {t.note && <span className="font-body text-xs text-muted">{t.note}</span>}
        <span className="font-display text-xl text-espresso sm:text-2xl">{formatPula(t.price)}</span>
        <ChevronRight size={16} className="text-muted transition group-hover:translate-x-0.5 group-hover:text-gold-deep" />
      </div>
    </button>
  );

  return (
    <>
      <Seo title="Treatments" description="Grace Haven Spa's full menu — massage, facials, nails, combos, makeup, and winter specials in Gaborone." />

      <PageHeader eyebrow="The Menu" title="Treatments" intro="Tap any treatment for details, or to book it. Every treatment is unhurried and tailored to you. Prices are in Botswana Pula." />

      <section className="bg-cream py-24">
        <Container>
          <div className="space-y-20">
            {categories.map((cat) => (
              <div key={cat.id} className="reveal">
                <SectionHeading eyebrow={cat.title} title={cat.title} intro={cat.blurb} />
                <div className="mt-8 divide-y divide-line border-y border-line">
                  {cat.treatments.map((t) => <Row key={t.id} t={t} />)}
                </div>
              </div>
            ))}

            <div className="reveal rounded-sm border border-blush/50 bg-blush-soft/40 p-8 sm:p-10">
              <SectionHeading eyebrow="Limited time" title="Winter Specials" intro="Seasonal pairings at a special price — tap to book." />
              <div className="mt-6 divide-y divide-blush/40">
                {winterSpecials.map((t) => (
                  <button key={t.id} onClick={() => setActive(t)} className="group flex w-full items-baseline justify-between gap-6 py-4 text-left">
                    <h3 className="font-body text-base text-espresso group-hover:text-blush-deep">{t.name}</h3>
                    <span className="flex items-baseline gap-2"><span className="font-display text-xl text-blush-deep">{formatPula(t.price)}</span><ChevronRight size={15} className="text-blush-deep/60" /></span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-20 text-center">
            <Link to="/booking" className="btn-primary">Book a treatment</Link>
          </div>
        </Container>
      </section>

      {/* Treatment detail modal */}
      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-espresso-deep/60 p-4" onClick={() => setActive(null)}>
          <div className="w-full max-w-md border border-line bg-cream p-8 shadow-soft" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-3xl text-espresso">{active.name}</h2>
                <div className="mt-2 flex items-center gap-3 font-body text-sm text-muted">
                  {active.durationMin && <span className="inline-flex items-center gap-1"><Clock size={14} /> {active.durationMin} min</span>}
                  <span className="font-display text-lg text-gold-deep">{active.note ? `${active.note} ` : ''}{formatPula(active.price)}</span>
                </div>
              </div>
              <button onClick={() => setActive(null)} className="text-muted hover:text-espresso"><X size={22} /></button>
            </div>
            <div className="rule-gold mt-4" />
            <p className="mt-5 font-body text-sm leading-relaxed text-muted">{active.description || FALLBACK}</p>
            <div className="mt-7 flex flex-col gap-2.5 sm:flex-row">
              <Link to={`/booking?add=${active.id}`} className="btn-primary flex-1 justify-center">Book this treatment <ArrowRight size={15} /></Link>
              <button onClick={() => setActive(null)} className="btn-outline flex-1 justify-center">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
